import { useQuery } from "@tanstack/react-query";
import { useDebounceValue } from "usehooks-ts";
import ApiHttp from "byzantine/src/ApiHttp";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TransactionStats {
  transaction_count: number;
  /** Sum of credit + debit amounts in cents */
  net_total: number;
  /** Sum of credit amounts in cents */
  credits: number;
  /** Sum of debit amounts in cents (typically negative) */
  debits: number;
}

export interface UseTransactionStatsOptions {
  selectedAccountIds: string[];
  minDate: string;
  maxDate: string;
  selectedTypeCodes: string[];
}

export interface UseTransactionStatsResult {
  stats: TransactionStats | null;
  isLoading: boolean;
  error: string | null;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DEBOUNCE_MS = 300;

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useTransactionStats = ({
  selectedAccountIds,
  minDate,
  maxDate,
  selectedTypeCodes,
}: UseTransactionStatsOptions): UseTransactionStatsResult => {
  // Debounce all filter inputs so rapid changes result in a single request.
  // useDebounceValue returns [debouncedValue, setValue] — we only need the value.
  const [debouncedAccountIds] = useDebounceValue(
    selectedAccountIds,
    DEBOUNCE_MS
  );
  const [debouncedMinDate] = useDebounceValue(minDate, DEBOUNCE_MS);
  const [debouncedMaxDate] = useDebounceValue(maxDate, DEBOUNCE_MS);
  const [debouncedTypeCodes] = useDebounceValue(selectedTypeCodes, DEBOUNCE_MS);

  // Gate on debounced values for the query (avoids firing requests mid-debounce)
  // but use the raw selectedAccountIds for the return values so the UI clears
  // stats/error immediately when the user deselects all accounts.
  const hasDebouncedAccounts = debouncedAccountIds.length > 0;
  const hasRawAccounts = selectedAccountIds.length > 0;

  const { data, isFetching, error } = useQuery<TransactionStats>({
    queryKey: [
      "transactionStats",
      debouncedAccountIds,
      debouncedMinDate,
      debouncedMaxDate,
      debouncedTypeCodes,
    ],
    queryFn: async () => {
      const payload: {
        account_uuids: string[];
        min_date: string;
        max_date: string;
        bai_codes?: string[];
      } = {
        account_uuids: debouncedAccountIds,
        min_date: debouncedMinDate,
        max_date: debouncedMaxDate,
      };

      if (debouncedTypeCodes.length > 0) {
        payload.bai_codes = debouncedTypeCodes;
      }

      return ApiHttp.fetch<TransactionStats>(
        "reports/transactions_stats",
        { method: "POST" },
        payload
      );
    },
    enabled: hasDebouncedAccounts,
    retry: false,
  });

  let errorMessage: string | null = null;
  if (hasRawAccounts && error) {
    errorMessage = error instanceof Error ? error.message : String(error);
  }

  return {
    stats: hasRawAccounts ? data ?? null : null,
    isLoading: hasRawAccounts && isFetching,
    error: errorMessage,
  };
};
